package com.dotdrives.web.ui

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import org.w3c.dom.HTMLElement
import react.useEffect
import react.useRef

/** Contains focus in visible controls and restores the opener after dismissal. */
fun useDialogFocus(open: Boolean, id: String, onClose: () -> Unit) =
    useDialogFocus(open, id, active = true, onClose = onClose)

fun useDialogFocus(open: Boolean, id: String, active: Boolean, onClose: () -> Unit) {
    val latestClose = useRef(onClose)
    latestClose.current = onClose
    useEffect(open, id) {
        if (open) {
            val prior = document.activeElement
            val dialog = document.getElementById(id)
            try {
                awaitCancellation()
            } finally {
                window.setTimeout({
                    if (dialog?.isConnected != true && prior?.isConnected == true) prior.asDynamic().focus()
                }, 0)
            }
        }
    }
    useEffect(open, active, id) {
        if (open && active) {
            val dialog = document.getElementById(id)
            // Inert siblings at every ancestor keep background controls out of keyboard and
            // pointer navigation without inerting the dialog's own React tree.
            val background = mutableListOf<Pair<org.w3c.dom.Element, Boolean>>()
            var branch: org.w3c.dom.Element? = dialog
            while (branch != null && branch != document.body) {
                val parent = branch.parentElement ?: break
                val children = parent.children
                for (index in 0 until children.length) {
                    val sibling = children.item(index) ?: continue
                    if (sibling != branch) {
                        background += sibling to sibling.hasAttribute("inert")
                        sibling.setAttribute("inert", "")
                    }
                }
                branch = parent
            }
            fun controls(): List<HTMLElement> {
                val nodes = dialog?.querySelectorAll("button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]") ?: return emptyList()
                return (0 until nodes.length).mapNotNull { nodes.item(it) as? HTMLElement }
                    .filter { it.tabIndex >= 0 && !it.hidden && (it.asDynamic().getClientRects().length as Int) > 0 && window.getComputedStyle(it).visibility != "hidden" }
            }
            if (dialog != null) {
                dialog.asDynamic().tabIndex = -1
                if (document.activeElement !in controls()) (controls().firstOrNull() ?: dialog).asDynamic().focus()
            }
            val listener: (dynamic) -> Unit = { event ->
                if (event.key == "Escape") { event.preventDefault(); latestClose.current?.invoke() }
                if (event.key == "Tab") {
                    val nodes = controls()
                    val active = document.activeElement
                    if (nodes.isEmpty()) {
                        event.preventDefault(); dialog?.asDynamic()?.focus()
                    } else if (event.shiftKey == true && (active == nodes.first() || active !in nodes)) {
                        event.preventDefault(); nodes.last().focus()
                    } else if (event.shiftKey != true && (active == nodes.last() || active !in nodes)) {
                        event.preventDefault(); nodes.first().focus()
                    }
                }
            }
            window.asDynamic().addEventListener("keydown", listener)
            try { awaitCancellation() } finally {
                window.asDynamic().removeEventListener("keydown", listener)
                background.forEach { (element, wasInert) -> if (!wasInert) element.removeAttribute("inert") }
            }
        }
    }
}
